import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dropdown } from "primereact/dropdown";
import { SlCheck } from "react-icons/sl";
import { BsExclamationTriangleFill, BsArrowRight } from "react-icons/bs";
import { HiCheck, HiArrowPath } from "react-icons/hi2";
import ParcelOrderService from "../../services/parcel-order.service";
import ParcelService from "../../services/parcel.service";
import ProvinceService from "../../services/province.service";
import TownService from "../../services/town.service";
import AddressInput from "../../components/AddressInput";
import AuthService from "../../services/auth.service";
import { useSelectedBusiness } from "../../context/BusinessContext";

// ── Constants ─────────────────────────────────────────────────────────────────
const MIDRAND_COORDS = { lat: -25.9856, lng: 28.1289 };
const MAX_DISTANCE_KM = 70;

// ── Helpers ───────────────────────────────────────────────────────────────────
function haversine(lat1, lon1, lat2, lon2) {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

async function distanceFromMidrand(address) {
  // Try Google Distance Matrix first
  if (window.google?.maps) {
    try {
      return await new Promise((resolve, reject) => {
        new window.google.maps.DistanceMatrixService().getDistanceMatrix(
          {
            origins: [`${MIDRAND_COORDS.lat},${MIDRAND_COORDS.lng}`],
            destinations: [address],
            travelMode: window.google.maps.TravelMode.DRIVING,
            unitSystem: window.google.maps.UnitSystem.METRIC,
          },
          (res, status) => {
            if (status !== "OK") return reject(new Error(status));
            const el = res.rows[0].elements[0];
            if (el.status !== "OK") return reject(new Error(el.status));
            resolve(el.distance.value / 1000);
          },
        );
      });
    } catch (_) {}
  }
  // Fallback: geocoding + Haversine
  const data = await fetch(
    `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}&countrycodes=za&limit=1`,
  ).then((r) => r.json());
  if (!data?.length) throw new Error("Could not geocode address");
  return haversine(
    MIDRAND_COORDS.lat,
    MIDRAND_COORDS.lng,
    parseFloat(data[0].lat),
    parseFloat(data[0].lon),
  );
}

// ── Component ─────────────────────────────────────────────────────────────────
export default function CreateOrderPage() {
  const navigate = useNavigate();
  const user = AuthService.getCurrentUser()?.user;
  const { selectedBusiness: business } = useSelectedBusiness();

  // ── Distance type ──────────────────────────────────────────────────────────
  const [distanceType, setDistanceType] = useState("long");

  // ── Reference data ─────────────────────────────────────────────────────────
  const [provinces, setProvinces] = useState([]);
  const [destProvinces, setDestProvinces] = useState([]);
  const [depTowns, setDepTowns] = useState([]);
  const [destTowns, setDestTowns] = useState([]);
  const [parcels, setParcels] = useState([]);
  const [loadingRef, setLoadingRef] = useState(true);

  // ── Form fields ────────────────────────────────────────────────────────────
  const [depProvince, setDepProvince] = useState("");
  const [destProvince, setDestProvince] = useState("");
  const [depTown, setDepTown] = useState("");
  const [destTown, setDestTown] = useState("");
  const [depAddress, setDepAddress] = useState("");
  const [destAddress, setDestAddress] = useState("");
  const [parcelType, setParcelType] = useState(null); // parcel id
  const [deliveryType, setDeliveryType] = useState(null); // price id
  const [pickupLocation, setPickupLocation] = useState("");
  const [dropOffLocation, setDropOffLocation] = useState("");
  const [collectionDate, setCollectionDate] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [receiverName, setReceiverName] = useState("");
  const [receiverPhone, setReceiverPhone] = useState("");
  const [parcelDescription, setParcelDescription] = useState("");
  const [numberOfItems, setNumberOfItems] = useState("");
  const [collectionInvoiceNumber, setCollectionInvoiceNumber] = useState("");
  const [acceptedProhibited, setAcceptedProhibited] = useState(false);

  // ── Validation / UI state ──────────────────────────────────────────────────
  const [errors, setErrors] = useState({});
  const [distanceWarning, setDistanceWarning] = useState({ dep: "", dest: "" });
  const [showSummary, setShowSummary] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  // ── Permitted towns for Limpopo ────────────────────────────────────────────
  const PERMITTED_TOWNS = ["Giyani", "Malamulele", "Tzaneen", "Polokwane", "Bungeni", "Thohoyandou", "Phalaborwa"];

  // ── Load provinces + parcels on mount ─────────────────────────────────────
  useEffect(() => {
    Promise.all([ProvinceService.index(), ParcelService.index("long")])
      .then(([provs, p]) => {
        setProvinces(provs ?? []);
        setParcels(p ?? []);
      })
      .catch(() => {})
      .finally(() => setLoadingRef(false));
  }, []);

  // ── Reload parcels when distance type changes ──────────────────────────────
  useEffect(() => {
    ParcelService.index(distanceType)
      .then((p) => setParcels(p ?? []))
      .catch(() => {});
  }, [distanceType]);

  // ── Destination provinces (excluding departure) ────────────────────────────
  useEffect(() => {
    if (depProvince) {
      ProvinceService.excluding_province(depProvince)
        .then((p) => setDestProvinces(p ?? []))
        .catch(() => {});
    } else {
      setDestProvinces([]);
    }
  }, [depProvince]);

  // ── Departure towns ────────────────────────────────────────────────────────
  useEffect(() => {
    if (distanceType === "short") {
      TownService.by_province("1")
        .then((t) => {
          setDepTowns(t ?? []);
          setDestTowns(t ?? []);
        })
        .catch(() => {});
    } else if (depProvince) {
      TownService.by_province(depProvince)
        .then((t) => {
          const filtered =
            depProvince.toString() === "2"
              ? t.filter((x) => PERMITTED_TOWNS.includes(x.name))
              : t;
          setDepTowns(filtered ?? []);
        })
        .catch(() => {});
    } else {
      setDepTowns([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [depProvince, distanceType]);

  // ── Destination towns ──────────────────────────────────────────────────────
  useEffect(() => {
    if (distanceType === "short") return;
    if (destProvince) {
      TownService.by_province(destProvince)
        .then((t) => {
          const filtered =
            destProvince.toString() === "2"
              ? t.filter((x) => PERMITTED_TOWNS.includes(x.name))
              : t;
          setDestTowns(filtered ?? []);
        })
        .catch(() => {});
    } else {
      setDestTowns([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [destProvince, distanceType]);

  // ── Distance validation (short mode) ──────────────────────────────────────
  async function validateDistance(address, field) {
    if (!address?.trim()) {
      setDistanceWarning((p) => ({ ...p, [field]: "" }));
      return;
    }
    try {
      const km = await distanceFromMidrand(address);
      const msg =
        km > MAX_DISTANCE_KM
          ? `This address is ~${Math.round(km)}km from our Midrand warehouse (Gallagher Business Exchange). We service within ${MAX_DISTANCE_KM}km only.`
          : "";
      setDistanceWarning((p) => ({ ...p, [field]: msg }));
    } catch {
      setDistanceWarning((p) => ({ ...p, [field]: "" }));
    }
  }

  // ── Switch distance type ───────────────────────────────────────────────────
  function switchDistanceType(type) {
    if (type === distanceType) return;
    setDistanceType(type);
    setDepProvince(type === "short" ? "1" : "");
    setDestProvince(type === "short" ? "1" : "");
    setDepTown("");
    setDestTown("");
    setDepAddress("");
    setDestAddress("");
    setErrors({});
    setShowSummary(false);
    setDistanceWarning({ dep: "", dest: "" });
  }

  // ── Select parcel card ─────────────────────────────────────────────────────
  function selectParcel(parcel) {
    setParcelType(parcel.id);
    const sorted = [...(parcel.parcel_prices ?? [])].sort(
      (a, b) => a.price.value - b.price.value,
    );
    setDeliveryType(sorted[0]?.price?.id ?? null);
    setErrors((p) => ({ ...p, parcel_type: "" }));
    setShowSummary(false);
  }

  // ── Validate ───────────────────────────────────────────────────────────────
  function validate() {
    const e = {};
    if (distanceType === "long") {
      if (!depProvince) e.dep_province = "Please select departure province";
      if (!destProvince) e.dest_province = "Please select destination province";
      if (!depTown) e.dep_town = "Please select departure town";
      if (!destTown) e.dest_town = "Please select destination town";
      if (!pickupLocation.trim()) e.pickup = "Please enter a pickup address";
    } else {
      if (!depAddress.trim()) e.dep_address = "Please enter pickup address";
      if (!destAddress.trim()) e.dest_address = "Please enter delivery address";
      if (distanceWarning.dep || distanceWarning.dest)
        e.distance = "One or more addresses are outside our service area";
    }
    if (!receiverName.trim()) e.receiver_name = "Please enter receiver name";
    if (!receiverPhone.trim()) e.receiver_phone = "Please enter receiver phone number";
    if (!parcelType) e.parcel_type = "Please select a package type";
    if (!collectionDate) e.collection_date = "Please select a collection date";
    if (!acceptedProhibited)
      e.prohibited = "You must confirm you are not sending prohibited items";
    return e;
  }

  // ── Submit ─────────────────────────────────────────────────────────────────
  async function handleSubmit(e) {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      setShowSummary(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    setErrors({});
    setShowSummary(false);
    setSubmitting(true);
    setSubmitError("");
    try {
      const result = await ParcelOrderService.createForBusiness(
        business?.id,
        user?.id,
        parcelType,
        deliveryType,
        distanceType === "long" ? pickupLocation : depAddress,
        quantity,
        collectionDate,
        distanceType === "short" ? "1" : depProvince,
        distanceType === "short" ? "1" : destProvince,
        depTown,
        destTown,
        depAddress,
        distanceType === "long" ? dropOffLocation : destAddress,
        receiverName,
        receiverPhone,
        parcelDescription,
        numberOfItems,
        collectionInvoiceNumber,
      );
      const selectedParcel = parcels.find((p) => p.id === parcelType);
      const selectedPrice = selectedParcel?.parcel_prices?.find(
        (pp) => pp.price?.id === deliveryType
      );
      const totalValue = (selectedPrice?.price?.value ?? 0) * quantity;
      localStorage.setItem("parcelin_pending_order", JSON.stringify({
        value: totalValue,
        reference: result.reference_number || "",
      }));
      window.location.assign(result.payment_url);
    } catch (err) {
      setSubmitError(
        err?.response?.data?.message ??
          "Something went wrong. Please try again.",
      );
      setSubmitting(false);
    }
  }

  // ── Form ───────────────────────────────────────────────────────────────────
  return (
    <div className="px-4 py-8 lg:px-8 ">
      <div className="mb-6">
        <h1 className="text-2xl font-extrabold text-gray-900">New Order</h1>
        <p className="text-sm text-gray-400 mt-0.5">
          {business?.name
            ? `Creating on behalf of ${business.name}`
            : "Fill in the shipment details below"}
        </p>
      </div>

      {submitError && (
        <div className="mb-5 p-4 rounded-xl bg-red-50 border border-red-100 text-red-700 text-sm">
          {submitError}
        </div>
      )}

      <form
        onSubmit={handleSubmit}
        className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden"
      >
        {/* Header band */}
        <div className="bg-gradient-to-br from-violet-700 via-violet-600 to-violet-500 px-6 py-5 relative overflow-hidden">
          <div className="absolute -top-6 -right-6 w-28 h-28 bg-white/10 rounded-full" />
          <div className="absolute -bottom-8 -left-4 w-20 h-20 bg-white/5 rounded-full" />
          <h2 className="text-xl font-extrabold text-white relative">
            New Business Order
          </h2>
          <p className="text-violet-200 text-sm mt-0.5 relative">
            Fast, affordable delivery across South Africa
          </p>
        </div>

        <div className="p-6 space-y-5">
          {/* Distance toggle */}
          <div className="flex rounded-xl bg-gray-100 p-1 gap-1">
            {["long", "short"].map((type) => (
              <button
                key={type}
                type="button"
                onClick={() => switchDistanceType(type)}
                className={`flex-1 py-2.5 text-sm font-semibold rounded-lg transition-all duration-200 ${
                  distanceType === type
                    ? "bg-white shadow text-violet-700"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                {type === "long" ? "Long Distance" : "Short Distance"}
              </button>
            ))}
          </div>

          {/* Validation summary */}
          {showSummary && Object.keys(errors).length > 0 && (
            <div className="bg-red-50 border-2 border-red-400 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <BsExclamationTriangleFill
                  className="text-red-500 mt-0.5 flex-shrink-0"
                  size={18}
                />
                <div>
                  <p className="font-bold text-red-800 text-sm mb-1">
                    Please complete all required fields
                  </p>
                  <ul className="text-sm text-red-700 list-disc list-inside space-y-0.5">
                    {Object.values(errors).map(
                      (msg, i) => msg && <li key={i}>{msg}</li>,
                    )}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* ── Long distance ── */}
          {distanceType === "long" && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    From <span className="text-red-500">*</span>
                  </label>
                  <div
                    className={
                      errors.dep_province
                        ? "ring-2 ring-red-400 rounded-lg"
                        : ""
                    }
                  >
                    <Dropdown
                      value={depProvince}
                      onChange={(e) => {
                        setDepProvince(e.value);
                        setDepTown("");
                        setDestProvince("");
                        setDestTown("");
                        setErrors((p) => ({ ...p, dep_province: "" }));
                        setShowSummary(false);
                      }}
                      options={loadingRef ? [] : provinces}
                      optionLabel="name"
                      optionValue="id"
                      placeholder={loadingRef ? "Loading…" : "Province *"}
                      filter
                      className="w-full"
                      style={{ borderRadius: "0.5rem" }}
                    />
                  </div>
                  {errors.dep_province && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.dep_province}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    To <span className="text-red-500">*</span>
                  </label>
                  <div
                    className={
                      errors.dest_province
                        ? "ring-2 ring-red-400 rounded-lg"
                        : ""
                    }
                  >
                    <Dropdown
                      value={destProvince}
                      onChange={(e) => {
                        setDestProvince(e.value);
                        setDestTown("");
                        setErrors((p) => ({ ...p, dest_province: "" }));
                        setShowSummary(false);
                      }}
                      options={depProvince ? destProvinces : provinces}
                      optionLabel="name"
                      optionValue="id"
                      placeholder={loadingRef ? "Loading…" : "Province *"}
                      filter
                      className="w-full"
                      style={{ borderRadius: "0.5rem" }}
                    />
                  </div>
                  {errors.dest_province && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.dest_province}
                    </p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div
                    className={
                      errors.dep_town ? "ring-2 ring-red-400 rounded-lg" : ""
                    }
                  >
                    <Dropdown
                      value={depTown}
                      onChange={(e) => {
                        setDepTown(e.value);
                        setErrors((p) => ({ ...p, dep_town: "" }));
                        setShowSummary(false);
                      }}
                      options={depTowns}
                      optionLabel="name"
                      optionValue="id"
                      placeholder="Town *"
                      filter
                      disabled={!depProvince}
                      className="w-full"
                      style={{ borderRadius: "0.5rem" }}
                    />
                  </div>
                  {errors.dep_town && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.dep_town}
                    </p>
                  )}
                </div>
                <div>
                  <div
                    className={
                      errors.dest_town ? "ring-2 ring-red-400 rounded-lg" : ""
                    }
                  >
                    <Dropdown
                      value={destTown}
                      onChange={(e) => {
                        setDestTown(e.value);
                        setErrors((p) => ({ ...p, dest_town: "" }));
                        setShowSummary(false);
                      }}
                      options={destTowns}
                      optionLabel="name"
                      optionValue="id"
                      placeholder="Town *"
                      filter
                      disabled={!destProvince}
                      className="w-full"
                      style={{ borderRadius: "0.5rem" }}
                    />
                  </div>
                  {errors.dest_town && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.dest_town}
                    </p>
                  )}
                </div>
              </div>

              {/* Pickup location (long distance only) */}
              <AddressInput
                id="pickup_location"
                name="pickup_location"
                value={pickupLocation}
                onChange={(e) => {
                  setPickupLocation(e.target.value);
                  setErrors((p) => ({ ...p, pickup: "" }));
                }}
                label="Pickup Address"
                placeholder="e.g. 123 Main Street, Cape Town"
                error={errors.pickup}
                touched={!!errors.pickup}
                showRequired={true}
              />
              <AddressInput
                id="drop_off_location"
                name="drop_off_location"
                value={dropOffLocation}
                onChange={(e) => {
                  setDropOffLocation(e.target.value);
                }}
                label="Drop-off Address"
                placeholder="Enter delivery address (optional)"
              />
            </>
          )}

          {/* ── Short distance ── */}
          {distanceType === "short" && (
            <>
              <div className="bg-violet-50 border border-violet-200 rounded-xl p-3">
                <p className="text-sm text-violet-800">
                  <strong>Short Distance Delivery:</strong> Available within
                  70km of our Midrand warehouse (Gallagher Business Exchange)
                </p>
              </div>

              <AddressInput
                id="dep_address"
                name="dep_address"
                value={depAddress}
                onChange={(e) => {
                  setDepAddress(e.target.value);
                  setErrors((p) => ({ ...p, dep_address: "" }));
                  setShowSummary(false);
                  setTimeout(
                    () => validateDistance(e.target.value, "dep"),
                    1000,
                  );
                }}
                onBlur={() => validateDistance(depAddress, "dep")}
                label="Pickup Address"
                placeholder="Enter pickup address"
                error={errors.dep_address}
                touched={true}
                showRequired={true}
              />
              {distanceWarning.dep && (
                <div className="bg-orange-50 border-2 border-orange-400 rounded-xl p-3 flex items-start gap-2">
                  <BsExclamationTriangleFill
                    className="text-orange-500 mt-0.5 flex-shrink-0"
                    size={16}
                  />
                  <p className="text-sm text-orange-800">
                    {distanceWarning.dep}
                  </p>
                </div>
              )}

              <AddressInput
                id="dest_address"
                name="dest_address"
                value={destAddress}
                onChange={(e) => {
                  setDestAddress(e.target.value);
                  setErrors((p) => ({ ...p, dest_address: "" }));
                  setShowSummary(false);
                  setTimeout(
                    () => validateDistance(e.target.value, "dest"),
                    1000,
                  );
                }}
                onBlur={() => validateDistance(destAddress, "dest")}
                label="Delivery Address"
                placeholder="Enter delivery address"
                error={errors.dest_address}
                touched={true}
                showRequired={true}
              />
              {distanceWarning.dest && (
                <div className="bg-orange-50 border-2 border-orange-400 rounded-xl p-3 flex items-start gap-2">
                  <BsExclamationTriangleFill
                    className="text-orange-500 mt-0.5 flex-shrink-0"
                    size={16}
                  />
                  <p className="text-sm text-orange-800">
                    {distanceWarning.dest}
                  </p>
                </div>
              )}
            </>
          )}

          {/* Collection date + quantity */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Collection Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={collectionDate}
                min={new Date().toISOString().split("T")[0]}
                onChange={(e) => {
                  setCollectionDate(e.target.value);
                  setErrors((p) => ({ ...p, collection_date: "" }));
                }}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-violet-200 focus:border-violet-400"
              />
              {errors.collection_date && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.collection_date}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Quantity
              </label>
              <input
                type="number"
                min="1"
                value={quantity}
                onChange={(e) =>
                  setQuantity(Math.max(1, parseInt(e.target.value, 10) || 1))
                }
                className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-violet-200 focus:border-violet-400"
              />
            </div>
          </div>

          {/* Receiver details */}
          <div className="border-t border-gray-100 pt-4">
            <h3 className="text-sm font-bold text-gray-800 mb-3">Receiver Details</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Receiver Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={receiverName}
                  onChange={(e) => {
                    setReceiverName(e.target.value);
                    setErrors((p) => ({ ...p, receiver_name: "" }));
                  }}
                  placeholder="Full name"
                  className={`w-full px-3 py-2.5 border rounded-xl text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-violet-200 focus:border-violet-400 ${errors.receiver_name ? "border-red-400" : "border-gray-200"}`}
                />
                {errors.receiver_name && (
                  <p className="text-red-500 text-xs mt-1">{errors.receiver_name}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Receiver Phone <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  value={receiverPhone}
                  onChange={(e) => {
                    setReceiverPhone(e.target.value);
                    setErrors((p) => ({ ...p, receiver_phone: "" }));
                  }}
                  placeholder="e.g. 0791234567"
                  className={`w-full px-3 py-2.5 border rounded-xl text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-violet-200 focus:border-violet-400 ${errors.receiver_phone ? "border-red-400" : "border-gray-200"}`}
                />
                {errors.receiver_phone && (
                  <p className="text-red-500 text-xs mt-1">{errors.receiver_phone}</p>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 mt-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Parcel Description
                </label>
                <input
                  type="text"
                  value={parcelDescription}
                  onChange={(e) => setParcelDescription(e.target.value)}
                  placeholder="e.g. 10 x Beauty Creams"
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-violet-200 focus:border-violet-400"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Number of Items
                </label>
                <input
                  type="number"
                  min="1"
                  value={numberOfItems}
                  onChange={(e) => setNumberOfItems(e.target.value)}
                  placeholder="e.g. 5"
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-violet-200 focus:border-violet-400"
                />
              </div>
            </div>
            <div className="mt-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Collection Invoice # (optional)
              </label>
              <input
                type="text"
                value={collectionInvoiceNumber}
                onChange={(e) => setCollectionInvoiceNumber(e.target.value)}
                placeholder="e.g. INV-001"
                className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-violet-200 focus:border-violet-400"
              />
            </div>
          </div>

          {/* Package selection */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              Select Package Size <span className="text-red-500">*</span>
            </label>
            {errors.parcel_type && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded-xl text-sm mb-3">
                {errors.parcel_type}
              </div>
            )}
            {loadingRef ? (
              <div className="grid grid-cols-3 gap-3">
                {[...Array(3)].map((_, i) => (
                  <div
                    key={i}
                    className="animate-pulse bg-gray-100 rounded-xl h-24"
                  />
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {parcels.map((parcel) => (
                  <div
                    key={parcel.id}
                    onClick={() => selectParcel(parcel)}
                    className={`border-2 rounded-xl p-4 cursor-pointer transition-all ${
                      parcelType === parcel.id
                        ? "border-violet-600 bg-violet-50"
                        : "border-gray-200 hover:border-violet-300"
                    }`}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div className="font-semibold text-gray-800 text-sm">
                        {parcel.package_type}
                      </div>
                      {parcelType === parcel.id && (
                        <SlCheck
                          size={18}
                          className="text-violet-600 flex-shrink-0"
                        />
                      )}
                    </div>
                    {parcel.parcel_prices?.[0]?.price?.value != null && (
                      <div className="text-sm text-gray-600">
                        From R{parcel.parcel_prices[0].price.value}
                      </div>
                    )}
                    <div className="text-xs text-gray-400 mt-1">
                      {parcel.max_height} x {parcel.max_width} ·{" "}
                      {parcel.max_weight}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Prohibited items */}
          <div className="border-t border-gray-100 pt-4">
            <div
              className={`flex items-start gap-2 p-3 rounded-xl transition-all ${
                errors.prohibited
                  ? "bg-red-50 border border-red-400"
                  : "bg-gray-50 border border-gray-200"
              }`}
            >
              <input
                type="checkbox"
                id="prohibited"
                checked={acceptedProhibited}
                onChange={(e) => {
                  setAcceptedProhibited(e.target.checked);
                  setErrors((p) => ({ ...p, prohibited: "" }));
                  setShowSummary(false);
                }}
                className="mt-0.5 h-4 w-4 text-violet-600 border-gray-300 rounded focus:ring-violet-500 cursor-pointer"
              />
              <label
                htmlFor="prohibited"
                className="flex-1 cursor-pointer text-xs text-gray-700"
              >
                I confirm I'm not sending prohibited items (weapons, explosives,
                illegal substances, etc.){" "}
                <span className="text-red-500">*</span>
              </label>
            </div>
            {errors.prohibited && (
              <p className="text-red-500 text-xs mt-1.5 ml-1">
                {errors.prohibited}
              </p>
            )}
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-gradient-to-r from-violet-600 to-violet-500 hover:from-violet-700 hover:to-violet-600 disabled:opacity-50 text-white font-bold py-4 rounded-xl transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-2 group"
          >
            {submitting ? (
              <>
                <HiArrowPath className="h-4 w-4 animate-spin" />
                Redirecting to payment…
              </>
            ) : (
              <>
                Pay Now
                <BsArrowRight
                  size={18}
                  className="transition-transform group-hover:translate-x-1"
                />
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
